/**
 * Helpers for turning a thrown API error into something a user can act on.
 *
 * `lib/api.ts` attaches the parsed DRF error body to the thrown Error as
 * `responseData`. Components that reach for the axios-style `error.response.data`
 * silently get `undefined` and fall back to a generic message, which hides the
 * reason the request failed.
 */

/** The parsed DRF error body, if the client attached one. */
export function getApiErrorData(error: unknown): Record<string, unknown> | null {
  const data = (error as { responseData?: unknown })?.responseData;
  return data && typeof data === 'object' ? (data as Record<string, unknown>) : null;
}

/**
 * Best-effort human-readable message for a failed request.
 *
 * Prefers the keys DRF and our own views actually use (`error`, `detail`,
 * `non_field_errors`), then falls back to listing field errors.
 */
export function getApiErrorMessage(error: unknown, fallback: string): string {
  const data = getApiErrorData(error);
  if (!data) return fallback;

  const asText = (value: unknown): string =>
    Array.isArray(value) ? value.filter(Boolean).join(' ') : String(value);

  for (const key of ['error', 'detail', 'non_field_errors'] as const) {
    const value = data[key];
    if (value) {
      const text = asText(value).trim();
      if (text) return text;
    }
  }

  const fieldErrors = Object.entries(data)
    .filter(([, value]) => value !== null && value !== undefined && value !== '')
    .map(([field, value]) => `${field}: ${asText(value)}`);

  return fieldErrors.length > 0 ? fieldErrors.join('; ') : fallback;
}
